// FASTQ filename rules, ported from the backend so the UI can warn BEFORE an
// upload rather than after the pipeline has already mangled a name.
//
// Parity matters here: these are line-for-line ports of
//   to_file_key()        - libra/services/fastq_file_service.py  (the PAIRING key)
//   fastq_sample_name()  - libra/models.py                       (the sample LABEL)
// If either changes server-side, change it here and in
// src/utils/__tests__/fastqNaming.test.js, which mirrors the backend's own
// test_fastq_sample_name.py cases.
//
// The two serve different purposes and deliberately disagree: for
// "sampleadi_R1_001.fastq.gz" the pairing key is "sampleadi_001" (the chunk is
// kept, so chunk 001 and 002 stay separate pairs) while the sample label is
// "sampleadi".

// ---------------------------------------------------------------- pairing key

// Recognised FASTQ extension, optionally preceded by ".clean".
const EXT_RE = /(?:\.clean)?\.f(?:ast)?q(?:\.gz)?$/i
// R1/R2/I1/I2 as a DELIMITED token, so "_R12" (a lane-like token) is left
// intact. Global: the backend's re.sub replaces every occurrence.
const READ_TOKEN_RE = /[._-][RI][12](?=[._-]|$)/gi
// A bare read number, but only as the final token, so a multi-digit "_001"
// chunk or "_L02" lane is never mistaken for a read.
const BARE_READ_RE = /[._-][12]$/

// Two uploads become one R1/R2 pair if and only if this returns the same string
// for both. Strip the extension, then the read indicator, then a trailing bare
// read number. Delimiter-agnostic, so "s.R1.fastq.gz" pairs like "s_R1.fastq.gz".
export const toFileKey = (fileName) =>
  String(fileName).replace(EXT_RE, '').replace(READ_TOKEN_RE, '').replace(BARE_READ_RE, '')

// --------------------------------------------------------------- sample label

// Trailing read indicator only, so anything the sequencer put in front of it
// survives - including fields that contain a 1 or 2, like the MGI barcode in
// "V350406735_L02_1_1.fq.gz" or the year in "HASTA_2024_R1".
const READ_SUFFIX_RE = /[._-](?:[RI][12](?:_\d+)?|[12])(?:\.clean)?\.f(?:ast)?q(?:\.gz)?$/i

export const fastqSampleName = (fileName) => {
  const name = String(fileName)
  let stem = name.replace(READ_SUFFIX_RE, '')
  // No read indicator at all: keep the whole stem rather than the raw file name.
  if (stem === name) stem = name.replace(EXT_RE, '')
  return stem.replace(/\s+/g, '_')
}

// ------------------------------------------------------------ read direction

// Which read a file is (1 or 2), or null when the name carries no indicator.
// Derived from the same token toFileKey strips, so the two can never disagree.
export const detectRead = (fileName) => {
  const stem = String(fileName).replace(EXT_RE, '')
  const tokens = stem.match(new RegExp(READ_TOKEN_RE.source, 'gi'))
  if (tokens && tokens.length) return Number(tokens[tokens.length - 1].slice(-1))
  const bare = stem.match(BARE_READ_RE)
  if (bare) return Number(bare[0].slice(-1))
  return null
}

export const hasFastqExtension = (fileName) => EXT_RE.test(String(fileName))

// FastqFile.original_name / .name are CharField(max_length=128); a longer name
// fails on insert, so it is worth catching in the browser.
export const MAX_FASTQ_NAME_LENGTH = 128

// Middle-ellipsis for display. Long MGI/Illumina names blow out any list they
// are shown in, and the informative parts are at both ends (sample id at the
// front, read indicator and extension at the back), so trim the middle and keep
// the full name for the tooltip.
export const shortenFileName = (name, max = 30) => {
  const s = String(name)
  if (s.length <= max) return s
  const head = Math.ceil((max - 1) / 2)
  const tail = Math.floor((max - 1) / 2)
  return `${s.slice(0, head)}\u2026${s.slice(s.length - tail)}`
}

// ----------------------------------------------------------------- the checks

export const ISSUE = {
  EXTENSION: 'EXTENSION',
  TOO_LONG: 'TOO_LONG',
  NO_READ_TOKEN: 'NO_READ_TOKEN',
  DUPLICATE: 'DUPLICATE',
  AMBIGUOUS: 'AMBIGUOUS',
  SAME_READ: 'SAME_READ',
  UNPAIRED: 'UNPAIRED',
  SAMPLE_COLLISION: 'SAMPLE_COLLISION',
  WHITESPACE: 'WHITESPACE',
}

// Group the given names the way the backend would pair them, and report what
// will go wrong. `error` = the upload or pairing cannot succeed as named;
// `warning` = it will proceed but probably not as the user intends.
//
// Pairing happens across ALL of an organisation's still-unpaired files, not just
// this selection, so an UNPAIRED file is only a warning: its mate may already be
// on the server (or arrive in a later batch).
export const analyzeFastqNames = (fileNames) => {
  const names = (fileNames || []).map(String)
  const issues = []
  const add = (code, severity, message, files) => issues.push({ code, severity, message, files })

  // --- per-file checks ---
  const seen = new Map()
  names.forEach((name) => seen.set(name, (seen.get(name) || 0) + 1))
  const duplicates = [...seen.entries()].filter(([, n]) => n > 1).map(([name]) => name)
  if (duplicates.length) {
    add(
      ISSUE.DUPLICATE,
      'error',
      'The same file name appears twice. Pairing skips candidates with an identical name, so these will never pair.',
      duplicates,
    )
  }

  const badExt = names.filter((n) => !hasFastqExtension(n))
  if (badExt.length) {
    add(
      ISSUE.EXTENSION,
      'error',
      'Not a recognised FASTQ name. Use .fastq, .fq, .fastq.gz or .fq.gz (optionally .clean before the extension).',
      badExt,
    )
  }

  const tooLong = names.filter((n) => n.length > MAX_FASTQ_NAME_LENGTH)
  if (tooLong.length) {
    add(
      ISSUE.TOO_LONG,
      'error',
      `Longer than ${MAX_FASTQ_NAME_LENGTH} characters, which is the maximum the server stores. Shorten the name before uploading.`,
      tooLong,
    )
  }

  const noRead = names.filter((n) => hasFastqExtension(n) && detectRead(n) === null)
  if (noRead.length) {
    add(
      ISSUE.NO_READ_TOKEN,
      'error',
      'No read indicator, so this file can never find its mate and will sit unpaired. End the name with _R1/_R2, _1/_2 or .R1/.R2 before the extension.',
      noRead,
    )
  }

  const spaced = names.filter((n) => /\s/.test(n))
  if (spaced.length) {
    add(
      ISSUE.WHITESPACE,
      'warning',
      'Contains a space. The sample name will have each space replaced by an underscore, so the label will not match the file name.',
      spaced,
    )
  }

  // --- grouping, as the backend pairs ---
  const groups = new Map()
  names.forEach((name) => {
    if (!hasFastqExtension(name)) return
    const key = toFileKey(name)
    if (!groups.has(key)) groups.set(key, { key, sampleName: fastqSampleName(name), files: [] })
    groups.get(key).files.push({ name, read: detectRead(name) })
  })

  groups.forEach((group) => {
    const unique = [...new Set(group.files.map((f) => f.name))]
    if (unique.length > 2) {
      add(
        ISSUE.AMBIGUOUS,
        'error',
        `${unique.length} files reduce to the same pairing key "${group.key}". Only two can pair; the server takes the first match it finds and leaves the rest unpaired.`,
        unique,
      )
      return
    }
    if (unique.length === 2) {
      const reads = group.files.map((f) => f.read)
      if (reads[0] === reads[1]) {
        add(
          ISSUE.SAME_READ,
          'error',
          `Both files look like read ${reads[0]}. One of them should be the other read.`,
          unique,
        )
      }
      return
    }
    // exactly one file with a usable read token
    if (group.files[0].read !== null) {
      add(
        ISSUE.UNPAIRED,
        'warning',
        `No mate in this selection. It will stay unpaired until a file whose name also reduces to "${group.key}" is uploaded.`,
        unique,
      )
    }
  })

  // Distinct pairs that would land on the same sample label produce two
  // analyses with one name - the @RG SM collision that makes GATK's
  // DetermineGermlineContigPloidy fail with SampleAlreadyInCollection.
  const byLabel = new Map()
  groups.forEach((group) => {
    if (!byLabel.has(group.sampleName)) byLabel.set(group.sampleName, [])
    byLabel.get(group.sampleName).push(group)
  })
  byLabel.forEach((list, label) => {
    if (list.length > 1) {
      add(
        ISSUE.SAMPLE_COLLISION,
        'warning',
        `${list.length} separate pairs would all be named "${label}". They stay separate uploads, but the analyses share one sample name, which is hard to tell apart later and can collide in CNV calling.`,
        list.flatMap((g) => g.files.map((f) => f.name)),
      )
    }
  })

  return {
    groups: [...groups.values()],
    issues,
    errorCount: issues.filter((i) => i.severity === 'error').length,
    warningCount: issues.filter((i) => i.severity === 'warning').length,
  }
}

// Shown in the info popover next to the upload area.
export const FASTQ_NAMING_RULES = {
  accepted: ['.fastq', '.fq', '.fastq.gz', '.fq.gz', '.clean.fastq.gz'],
  readForms: ['_R1 / _R2', '_1 / _2', '.R1 / .R2', '-R1 / -R2', '_R1_001 / _R2_001'],
  examples: [
    { name: 'sample_R1.fastq.gz + sample_R2.fastq.gz', label: 'sample', ok: true },
    { name: 'sample_R1_001.fastq.gz + sample_R2_001.fastq.gz', label: 'sample', ok: true },
    { name: 'V350406735_L02_1_1.fq.gz + V350406735_L02_1_2.fq.gz', label: 'V350406735_L02_1', ok: true },
    { name: 'X.R1.clean.fastq.gz + X.R2.clean.fastq.gz', label: 'X', ok: true },
    { name: 'sample.fastq.gz', label: 'never pairs - no read indicator', ok: false },
    { name: 'sample_R1_001 + sample_R2_002', label: 'never pairs - chunk differs', ok: false },
  ],
  notes: [
    'The read indicator must be the last thing before the extension.',
    'Everything before it is kept, so an MGI barcode (…_L02_1_) or a year (HASTA_2024_) survives.',
    'A lane or chunk field (_L004, _001) is part of the identity: _R1_001 and _R1_002 are different pairs.',
    `Names are limited to ${MAX_FASTQ_NAME_LENGTH} characters.`,
  ],
}
